import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  Building2,
  CalendarDays,
  Clock3,
  FileDown,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";
import { getOvertimeAnalysis } from "../../../api/overtimeApi";
import Layout from "../../layout/Layout";
import "./OvertimeAnalysisPage.css";

const MONTHS = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];

const emptyAnalysis = {
  totals: { totalHours: 0, recordCount: 0, employeeCount: 0, departmentCount: 0 },
  departments: [],
  reasons: [],
  workAreas: [],
  assignmentBreakdown: [],
  monthlyTrend: [],
  employees: [],
};

const formatNumber = (value) =>
  new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(
    Number(value || 0)
  );

const getErrorMessage = (error) =>
  error?.response?.data?.detail ||
  error?.response?.data?.message ||
  "Analiz verileri alınamadı.";

function OvertimeAnalysisPage() {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [mode, setMode] = useState("month");
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [analysis, setAnalysis] = useState(emptyAnalysis);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadAnalysis = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await getOvertimeAnalysis({
        year,
        ...(mode === "month" ? { month } : {}),
      });
      setAnalysis(response.data.data || emptyAnalysis);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
      setAnalysis(emptyAnalysis);
    } finally {
      setLoading(false);
    }
  }, [mode, month, year]);

  useEffect(() => {
    loadAnalysis();
  }, [loadAnalysis]);

  const years = useMemo(
    () => Array.from({ length: 6 }, (_, index) => currentYear + 1 - index),
    [currentYear]
  );

  const monthlyTrend = useMemo(() => {
    const indexed = new Map(
      analysis.monthlyTrend.map((item) => [Number(item.month), item])
    );
    return MONTHS.map((label, index) => ({
      label,
      month: index + 1,
      recordCount: indexed.get(index + 1)?.recordCount || 0,
      totalHours: indexed.get(index + 1)?.totalHours || 0,
    }));
  }, [analysis.monthlyTrend]);

  const topDepartment = analysis.departments[0];
  const topWorkArea = analysis.workAreas[0];
  const topReason = analysis.reasons[0];
  const maxDepartmentCount = Math.max(
    1,
    ...analysis.departments.map((item) => item.recordCount)
  );
  const maxReasonCount = Math.max(
    1,
    ...analysis.reasons.map((item) => item.recordCount)
  );
  const maxMonthlyCount = Math.max(
    1,
    ...monthlyTrend.map((item) => item.recordCount)
  );
  const periodTitle =
    mode === "month" ? `${MONTHS[month - 1]} ${year}` : `${year} yılı`;

  return (
    <Layout>
      <div className="overtime-analysis-page">
        <section className="oa-hero">
          <div>
            <span className="oa-kicker">
              <BarChart3 size={16} /> Yönetim toplantısı görünümü
            </span>
            <h1>Mesai Analizi</h1>
            <p>
              Mesainin kim tarafından, hangi nedenle ve hangi çalışma alanına
              yönlendirildiğini aylık veya yıllık inceleyin.
            </p>
          </div>
          <div className="oa-hero-period">
            <small>Rapor dönemi</small>
            <strong>{periodTitle}</strong>
            <span>{analysis.totals.recordCount} mesai kaydı</span>
          </div>
        </section>

        <section className="oa-toolbar" aria-label="Analiz filtreleri">
          <div className="oa-mode-switch">
            <button
              className={mode === "month" ? "active" : ""}
              onClick={() => setMode("month")}
              type="button"
            >
              Aylık
            </button>
            <button
              className={mode === "year" ? "active" : ""}
              onClick={() => setMode("year")}
              type="button"
            >
              Yıllık
            </button>
          </div>
          <label>
            <span>Yıl</span>
            <select value={year} onChange={(event) => setYear(Number(event.target.value))}>
              {years.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </label>
          {mode === "month" && (
            <label>
              <span>Ay</span>
              <select value={month} onChange={(event) => setMonth(Number(event.target.value))}>
                {MONTHS.map((item, index) => (
                  <option key={item} value={index + 1}>{item}</option>
                ))}
              </select>
            </label>
          )}
          <button className="oa-print-button" type="button" onClick={() => window.print()}>
            <FileDown size={17} /> Toplantı çıktısı
          </button>
        </section>

        {error && <div className="oa-alert">{error}</div>}

        {loading ? (
          <div className="oa-loading"><span /> Analiz hazırlanıyor...</div>
        ) : (
          <>
            <section className="oa-kpi-grid">
              <KpiCard icon={<CalendarDays />} label="Yazılan mesai" value={formatNumber(analysis.totals.recordCount)} suffix="kayıt" tone="blue" />
              <KpiCard icon={<Clock3 />} label="Toplam süre" value={formatNumber(analysis.totals.totalHours)} suffix="saat" tone="amber" />
              <KpiCard icon={<Users />} label="Mesai yapan" value={formatNumber(analysis.totals.employeeCount)} suffix="personel" tone="green" />
              <KpiCard icon={<Building2 />} label="Talep eden" value={formatNumber(analysis.totals.departmentCount)} suffix="departman" tone="purple" />
            </section>

            {analysis.totals.recordCount > 0 && (
              <section className="oa-insight-strip">
                <div className="oa-insight-icon"><TrendingUp size={22} /></div>
                <div>
                  <small>Dönemin yönetici özeti</small>
                  <p>
                    En çok mesaiyi <strong>{topDepartment?.department || "—"}</strong>{" "}
                    yazdırdı ({topDepartment?.recordCount || 0} kayıt). Personel en sık{" "}
                    <strong>{topWorkArea?.workArea || "—"}</strong> alanına yönlendirildi
                    ({topWorkArea?.recordCount || 0} kez, {formatNumber(topWorkArea?.totalHours)} saat).
                    Ana neden <strong>{topReason?.reasonLabel || "—"}</strong> oldu.
                  </p>
                </div>
              </section>
            )}

            {mode === "year" && (
              <section className="oa-panel oa-trend-panel">
                <PanelHeader title="Yıl içindeki hareket" description="Ay bazında mesai kayıt adedi ve toplam saat" />
                <div className="oa-month-chart">
                  {monthlyTrend.map((item) => (
                    <div className="oa-month-column" key={item.month}>
                      <div className="oa-month-values">
                        <strong>{item.recordCount}</strong>
                        <small>{formatNumber(item.totalHours)} sa</small>
                      </div>
                      <div className="oa-month-track">
                        <span style={{ height: `${(item.recordCount / maxMonthlyCount) * 100}%` }} />
                      </div>
                      <b>{item.label.slice(0, 3)}</b>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="oa-two-column">
              <div className="oa-panel">
                <PanelHeader title="Mesaiyi yazdıran departmanlar" description="Talebin kaynağı ve toplam etkisi" />
                <DistributionList items={analysis.departments} max={maxDepartmentCount} labelKey="department" />
              </div>
              <div className="oa-panel">
                <PanelHeader title="Mesai nedenleri" description="Kararın arkasındaki ana ihtiyaç" />
                <DistributionList items={analysis.reasons} max={maxReasonCount} labelKey="reasonLabel" green />
              </div>
            </section>

            <section className="oa-panel">
              <PanelHeader title="Personel nereye yönlendirildi?" description="Çalışma alanı bazında görevlendirme sayısı, süre ve personel" />
              <div className="oa-area-grid">
                {analysis.workAreas.map((item, index) => (
                  <article className="oa-area-card" key={item.workArea}>
                    <div className="oa-area-rank">{String(index + 1).padStart(2, "0")}</div>
                    <div>
                      <h3>{item.workArea}</h3>
                      <p>{item.recordCount} kez görevlendirildi</p>
                    </div>
                    <div className="oa-area-metrics">
                      <strong>{formatNumber(item.totalHours)} saat</strong>
                      <span>{item.employeeCount} personel</span>
                    </div>
                  </article>
                ))}
                {!analysis.workAreas.length && <EmptyState />}
              </div>
            </section>

            <section className="oa-panel">
              <PanelHeader title="Nereye, neden gönderildi?" description="Toplantıda kök nedeni görmek için çalışma alanı ve neden detayı kırılımı" />
              <div className="oa-table-wrap">
                <table className="oa-table">
                  <thead>
                    <tr><th>Çalışma alanı</th><th>Ana neden</th><th>Neden detayı</th><th>Personel</th><th>Kayıt</th><th>Süre</th></tr>
                  </thead>
                  <tbody>
                    {analysis.assignmentBreakdown.map((item) => (
                      <tr key={`${item.workArea}-${item.reasonType}-${item.reasonDetail}`}>
                        <td><strong>{item.workArea}</strong></td>
                        <td>{item.reasonLabel}</td>
                        <td>{item.reasonDetail}</td>
                        <td>{item.employeeCount}</td>
                        <td><span className="oa-count-badge">{item.recordCount}</span></td>
                        <td><strong>{formatNumber(item.totalHours)} sa</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!analysis.assignmentBreakdown.length && <EmptyState />}
              </div>
            </section>

            <section className="oa-panel">
              <PanelHeader title="En çok mesai yapan personel" description="Toplam süreye göre ilk 10 personel" />
              <div className="oa-table-wrap">
                <table className="oa-table oa-employee-table">
                  <thead><tr><th>Personel</th><th>Ana bölümü</th><th>Farklı çalışma alanı</th><th>Kayıt</th><th>Toplam süre</th></tr></thead>
                  <tbody>
                    {analysis.employees.map((item) => (
                      <tr key={item.employeeId || item.fullName}>
                        <td><strong>{item.fullName}</strong></td>
                        <td>{item.department}</td>
                        <td>{item.workAreaCount}</td>
                        <td>{item.recordCount}</td>
                        <td><strong>{formatNumber(item.totalHours)} saat</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!analysis.employees.length && <EmptyState />}
              </div>
            </section>
          </>
        )}
      </div>
    </Layout>
  );
}

function KpiCard({ icon, label, value, suffix, tone }) {
  return (
    <article className={`oa-kpi oa-kpi--${tone}`}>
      <div className="oa-kpi-icon">{icon}</div>
      <div><span>{label}</span><strong>{value}</strong><small>{suffix}</small></div>
    </article>
  );
}

function PanelHeader({ title, description }) {
  return <header className="oa-panel-header"><div><h2>{title}</h2><p>{description}</p></div><Target size={19} /></header>;
}

function DistributionList({ items, max, labelKey, green = false }) {
  if (!items.length) return <EmptyState />;
  return (
    <div className="oa-distribution-list">
      {items.map((item) => (
        <div className="oa-distribution-row" key={item[labelKey] || item.reasonType}>
          <div><strong>{item[labelKey]}</strong><span>{item.recordCount} kayıt · {formatNumber(item.totalHours)} saat</span></div>
          <div className="oa-distribution-track"><span className={green ? "green" : ""} style={{ width: `${(item.recordCount / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return <div className="oa-empty">Seçilen dönemde analiz edilecek kayıt bulunamadı.</div>;
}

export default OvertimeAnalysisPage;
